// Text policy for nicknames and loop titles (SPEC §6.5): a length/character policy plus a word
// list (English + German starter lists, extendable by the admin via config `text.extraWords`).
// Matching normalises case, accents and common look-alike characters ("l33t"), checks whole
// words for short entries and substrings for long ones (to avoid flagging innocent words). An
// entry ending in "*" is matched as a substring whatever its length.

/** Starter lists. Kept deliberately short; extend through the admin config. */
const EN = [
  "fuck*",
  "fucker",
  "fucking",
  "motherfucker",
  "shit*",
  "bullshit",
  "cunt",
  "bitch",
  "bastard",
  "asshole",
  "arsehole",
  "dick",
  "dickhead",
  "cock",
  "cocksucker",
  "pussy",
  "twat",
  "wanker",
  "whore",
  "slut",
  "nigger",
  "nigga",
  "faggot",
  "fag",
  "retard",
  "spastic",
  "kike",
  "chink",
  "spic",
  "wetback",
  "tranny",
  "dyke",
  "rapist",
  "rape",
  "nazi",
  "hitler",
  "heil",
  "kkk",
  "porn",
  "porno",
  "pedo",
  "paedo",
  "pedophile",
  "cum",
  "jizz",
  "anal",
  "dildo",
  "boner",
  "tits",
  "titties",
];
const DE = [
  "arschloch",
  "arsch",
  "fotze",
  "hure",
  "hurensohn",
  "schlampe",
  "wichser",
  "ficken",
  "fick",
  "scheisse",
  "scheiße",
  "missgeburt",
  "spast",
  "behindert",
  "schwuchtel",
  "kanake",
  "neger",
  "zigeuner",
  "judensau",
  "sieg heil",
  "nutte",
  "pisser",
  "mongo",
  "drecksau",
  "vergewaltiger",
  "kinderficker",
];

export const STARTER_WORDS: readonly string[] = [...EN, ...DE];

/** Names nobody may take (impersonation of the service or staff). */
export const RESERVED_NICKNAMES = [
  "admin",
  "administrator",
  "root",
  "system",
  "moderator",
  "mod",
  "staff",
  "support",
  "endlessbeat",
  "endless_beat",
  "endless-beat",
  "endless.beat",
  "wizard",
  "the_wizard",
  "former_contributor",
  "formercontributor",
  "anonymous",
  "null",
  "undefined",
  "official",
  "team",
];

const LOOKALIKE: Record<string, string> = {
  "0": "o",
  "1": "i",
  "!": "i",
  "3": "e",
  "4": "a",
  "@": "a",
  "5": "s",
  $: "s",
  "7": "t",
  "8": "b",
  "9": "g",
  "|": "i",
  "+": "t",
};

/** Lower-case, strip accents, map look-alikes; keeps letters/digits/spaces. */
export function normaliseText(s: string): string {
  const lower = s.toLowerCase().replace(/ß/g, "ss");
  let out = "";
  for (const ch of lower.normalize("NFKD").replace(/\p{M}/gu, "")) out += LOOKALIKE[ch] ?? ch;
  return out
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Squashed form without separators (catches "f.u.c.k" and "f u c k"). */
function squash(s: string): string {
  return s.replace(/ /g, "");
}

export interface PolicyResult {
  ok: boolean;
  /** Plain-language reason when not ok. */
  reason?: string;
}

/** Does `text` contain a listed word? */
export function containsBlockedWord(text: string, extra: readonly string[] = []): string | null {
  const norm = normaliseText(text);
  const words = norm.split(" ").filter(Boolean);
  const flat = squash(norm);
  for (const raw of [...STARTER_WORDS, ...extra]) {
    const anywhere = raw.endsWith("*");
    const w = normaliseText(anywhere ? raw.slice(0, -1) : raw);
    if (!w) continue;
    const ws = squash(w);
    if (anywhere) {
      if (flat.includes(ws)) return raw;
    } else if (w.includes(" ")) {
      if (norm.includes(w) || flat.includes(ws)) return raw;
    } else if (ws.length <= 4) {
      // Short words only as whole words (so "class", "scrap", "Dickens" stay fine).
      if (words.includes(ws)) return raw;
      if (words.length > 1 && words.every((x) => x.length === 1) && flat === ws) return raw; // "f u c k"
    } else if (flat.includes(ws)) return raw;
  }
  return null;
}

export const NICKNAME_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{1,22}[A-Za-z0-9]$/;

export function nicknameKey(nick: string): string {
  return nick.toLowerCase();
}

export function checkNickname(nick: string, extra: readonly string[] = []): PolicyResult {
  if (nick.length < 3 || nick.length > 24) return { ok: false, reason: "Nicknames are 3 to 24 characters long." };
  if (!NICKNAME_RE.test(nick))
    return {
      ok: false,
      reason: "Use letters, digits, dots, dashes or underscores, starting and ending with a letter or digit.",
    };
  if (/[._-]{2,}/.test(nick)) return { ok: false, reason: "Don't put two dots, dashes or underscores in a row." };
  const key = nicknameKey(nick);
  if (RESERVED_NICKNAMES.includes(key) || RESERVED_NICKNAMES.includes(key.replace(/[._-]/g, "")))
    return { ok: false, reason: "That nickname is reserved." };
  if (containsBlockedWord(nick.replace(/[._-]/g, " "), extra) || containsBlockedWord(nick, extra))
    return { ok: false, reason: "That nickname isn't allowed. Please choose another one." };
  return { ok: true };
}

export function checkTitle(title: string, extra: readonly string[] = []): PolicyResult {
  const t = title.trim();
  if (t.length === 0) return { ok: true };
  if (t.length > 40) return { ok: false, reason: "Titles are at most 40 characters." };
  // Printable text only: no control characters, no invisible format characters.
  if (/[\p{Cc}\p{Cf}\p{Co}\p{Cn}]/u.test(t))
    return { ok: false, reason: "The title contains characters that can't be shown." };
  if (/(https?:\/\/|www\.|\.[a-z]{2,}\/)/i.test(t)) return { ok: false, reason: "Titles can't contain links." };
  if (containsBlockedWord(t, extra))
    return { ok: false, reason: "That title isn't allowed. Please choose another one." };
  return { ok: true };
}

export const BIO_MAX = 280;

/** A profile bio: up to 280 printable characters (line breaks allowed), no links, no blocked words. */
export function checkBio(bio: string, extra: readonly string[] = []): PolicyResult {
  const t = bio.trim();
  if (t.length === 0) return { ok: true };
  if (t.length > BIO_MAX) return { ok: false, reason: `A bio is at most ${BIO_MAX} characters.` };
  // Printable text and line breaks only.
  if (/[\p{Cc}\p{Cf}\p{Co}\p{Cn}]/u.test(t.replace(/\n/g, "")))
    return { ok: false, reason: "The bio contains characters that can't be shown." };
  if ((t.match(/\n/g) ?? []).length > 4) return { ok: false, reason: "Please use at most five lines." };
  if (/(https?:\/\/|www\.|\.[a-z]{2,}\/)/i.test(t))
    return { ok: false, reason: "Put a link in the link field, not in the bio." };
  if (containsBlockedWord(t, extra)) return { ok: false, reason: "That bio isn't allowed. Please change it." };
  return { ok: true };
}

/** A profile link: one https URL (≤ 200 characters) without credentials, or empty. */
export function checkProfileLink(link: string, extra: readonly string[] = []): PolicyResult {
  const t = link.trim();
  if (t.length === 0) return { ok: true };
  if (t.length > 200) return { ok: false, reason: "Links are at most 200 characters." };
  let u: URL;
  try {
    u = new URL(t);
  } catch {
    return { ok: false, reason: "That doesn't look like a web address (it should start with https://)." };
  }
  if (u.protocol !== "https:") return { ok: false, reason: "Only https:// links are allowed." };
  if (u.username || u.password) return { ok: false, reason: "Links can't contain a user name or password." };
  if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(u.hostname)) return { ok: false, reason: "That web address isn't valid." };
  if (containsBlockedWord(t, extra)) return { ok: false, reason: "That link isn't allowed." };
  return { ok: true };
}
